export interface DashboardTemplate {
  id: string;
  label: string;
  category: "Regional Buyer" | "Weight Class";
  description: string;
  defaultRegion: string;
  defaultWeightClass: string;
  defaultDateRange: string;
  pinnedWidgets: string[];
  cmeExpanded: boolean;
}

// Core widgets that every template should include at the top.
// "market-snapshot" (Cash Feeder Market) removed 2026-04-14 — see note in
// dashboardWidgets.ts. DVAuctionTicker covers the same ground with proper
// weight-class × cattle-type separation.
const CORE_WIDGETS = [
  "market-index",
  "market-strength",
  "dv-auction-ticker",
];

export const DASHBOARD_TEMPLATES: DashboardTemplate[] = [
  // ── Regional Buyer ──
  {
    id: "plains",
    label: "Plains",
    category: "Regional Buyer",
    description: "CO, KS, MT, ND, NE, NM, OK, SD, TX, WY — the feedlot corridor",
    defaultRegion: "Plains",
    defaultWeightClass: "all-weights",
    defaultDateRange: "30d",
    pinnedWidgets: [...CORE_WIDGETS, "price-map", "best-sale-scanner", "weight-demand-heatmap"],
    cmeExpanded: false,
  },
  {
    id: "cornbelt-se",
    label: "Cornbelt + Southeast",
    category: "Regional Buyer",
    description: "IA, IL, MO, MN, AL, AR, GA, KY, TN & more — cow-calf source region",
    defaultRegion: "Cornbelt+SE",
    defaultWeightClass: "all-weights",
    defaultDateRange: "30d",
    pinnedWidgets: [...CORE_WIDGETS, "price-map", "best-sale-scanner", "weight-demand-heatmap"],
    cmeExpanded: false,
  },
  {
    id: "west",
    label: "West",
    category: "Regional Buyer",
    description: "AZ, CA, ID, NV, OR, UT, WA — mountain & pacific markets",
    defaultRegion: "West",
    defaultWeightClass: "all-weights",
    defaultDateRange: "30d",
    pinnedWidgets: [...CORE_WIDGETS, "price-map", "best-sale-scanner", "weight-demand-heatmap"],
    cmeExpanded: false,
  },

  // ── Weight Class Specialist ──
  {
    id: "lightweight-calves",
    label: "Lightweight Calves (300–499)",
    category: "Weight Class",
    description: "Focus on 300–499 lb calves — demand, slides & best prices",
    defaultRegion: "all-regions",
    defaultWeightClass: "300-499",
    defaultDateRange: "30d",
    pinnedWidgets: [...CORE_WIDGETS, "weight-demand-heatmap", "weight-slide", "best-sale-scanner"],
    cmeExpanded: false,
  },
  {
    id: "medium-feeders",
    label: "Medium Feeders (500–699)",
    category: "Weight Class",
    description: "500–699 lb feeders — demand heatmap, slides & placement margins",
    defaultRegion: "all-regions",
    defaultWeightClass: "500-699",
    defaultDateRange: "30d",
    pinnedWidgets: [...CORE_WIDGETS, "weight-demand-heatmap", "weight-slide", "best-sale-scanner", "placement-margin"],
    cmeExpanded: false,
  },
  {
    id: "heavy-feeders",
    label: "Heavy Feeders (700–899)",
    category: "Weight Class",
    description: "700–899 lb heavy feeders — demand, slides & margin forecasts",
    defaultRegion: "all-regions",
    defaultWeightClass: "700-899",
    defaultDateRange: "30d",
    pinnedWidgets: [...CORE_WIDGETS, "cme-settlements", "weight-demand-heatmap", "weight-slide", "placement-margin"],
    cmeExpanded: true,
  },
  {
    id: "yearlings",
    label: "Yearlings (900+)",
    category: "Weight Class",
    description: "900+ lb yearlings — demand trends, margins & CME basis",
    defaultRegion: "all-regions",
    defaultWeightClass: "900-999",
    defaultDateRange: "30d",
    pinnedWidgets: [...CORE_WIDGETS, "cme-settlements", "weight-demand-heatmap", "placement-margin"],
    cmeExpanded: true,
  },
];

export const TEMPLATE_MAP = Object.fromEntries(
  DASHBOARD_TEMPLATES.map(t => [t.id, t])
);
